import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import {
  dpdAnnahme,
  dpdLookup,
  dpdNotiz,
  dpdCodeAusTracking,
  dpdScanBarcode,
  istDpdLangBarcode,
  istDpdScanCode,
  dpdKurz,
  dpdPlatzhalterName,
  bekannterDpdName,
  anzahlBekannteDpdNamen,
  vorladenStart,
  dpdT,
} from '../services/dpd';
import { isAutoRegalLetter, getCarrierSortMode } from '../services/printerService';
import { autoRegalFach } from '../services/regalUnterfach';
import { uploadFoto } from '../services/mlService';
import { isTurboOn } from '../services/turbo';
import TurboAnzeige from './TurboAnzeige';
import FotoStand, { useFotoZaehler, fotosOffen } from './FotoStand';
import { mitWiederholung } from '../services/serverWarte';

const autoNachnameDpd = () => {
  const m = getCarrierSortMode('DPD');
  return m ? m === 'nachname' : isAutoRegalLetter();
};

async function fotoSichern(dataUrl) {
  try {
    return await uploadFoto(await (await fetch(dataUrl)).blob());
  } catch {
    return '';
  }
}

let audioCtx = null;
function ton(freq, ms, type = 'sine') {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.value = 0.2;
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + ms / 1000);
  } catch { /* kein Ton möglich */ }
}
const tonOk = () => ton(880, 120);
const tonWarn = () => { ton(660, 130); setTimeout(() => ton(660, 130), 180); };
const tonFehler = () => ton(220, 450, 'square');

/**
 * 📦 „DPD erfassen / DPD ontvangen": Barcodes der DPD-Pakete vom Fahrer nacheinander scannen.
 * Jeder Barcode (14-stellige Paketnummer oder 28-stelliger DPD-Leitcode) wird direkt bei DPD Pickup
 * (`POST event/v3` mit `DELIVERY`) gebucht; Empfängername und Regalfach kommen sofort aus dem
 * DPD-Pickup-Bestand (`pudoparcels/v3`) und das Etikett wird automatisch gedruckt.
 */
export default function DpdErfassen({ pakete, colors, language, onAnlegen, onNamen, onClose }) {
  const T = dpdT(language);
  const dark = colors?.darkMode;
  const f = {
    text: dark ? '#fff' : '#23272f',
    sub: dark ? '#aaa' : '#666',
    rand: dark ? '#444' : '#ddd',
    feld: dark ? '#1b1f27' : '#fff',
    zeile: dark ? '#2d333b' : '#f5f6f8',
  };

  const [fach, setFach] = useState('');
  const [hand, setHand] = useState('');
  const [eintraege, setEintraege] = useState([]); // { code, rawCode, stand, name, fach, grund }
  const [vorStand, setVorStand] = useState(() => {
    const n = anzahlBekannteDpdNamen();
    return n > 0 ? { done: true, ok: true, count: n } : { laeuft: true };
  });

  const fachRef = useRef('');
  fachRef.current = fach;
  const paketeRef = useRef(pakete);
  paketeRef.current = pakete;
  const anlegenRef = useRef(onAnlegen);
  anlegenRef.current = onAnlegen;
  const namenRef = useRef(onNamen);
  namenRef.current = onNamen;
  const gesehen = useRef(new Set());
  const fotos = useRef(new Map());
  const kette = useRef(Promise.resolve());
  const [fotoStand, verfolgeFoto] = useFotoZaehler();
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  // Vorab-Namen beim Öffnen frisch laden + offene „DPD 123456"-Platzhalter im Hintergrund auflösen
  useEffect(() => {
    let aktiv = true;
    (async () => {
      const r = await vorladenStart(false);
      if (!aktiv) return;
      if (r && r.ok) {
        setVorStand({ done: true, ok: true, count: r.count || anzahlBekannteDpdNamen() });
      } else {
        setVorStand({ done: true, ok: false });
      }
      const offene = (paketeRef.current || [])
        .filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && /^DPD \d{6}$/i.test(String(p.name || '').trim()) && p.trackingNumber)
        .slice(0, 20);
      if (!offene.length || !namenRef.current) return;
      const updates = [];
      for (const p of offene) {
        try {
          const bek = bekannterDpdName(p.trackingNumber);
          const nm = bek || (await dpdLookup(p.trackingNumber))?.name || '';
          if (nm) {
            const rFach = p.regalplatz || (autoNachnameDpd() ? autoRegalFach(nm) || '' : '');
            updates.push({ paket: p, code: dpdKurz(p.trackingNumber), name: nm, regalplatz: rFach || undefined });
          }
        } catch { /* */ }
      }
      if (updates.length && namenRef.current) {
        try { await namenRef.current(updates); } catch { /* */ }
      }
    })();
    return () => { aktiv = false; };
  }, []);

  const setze = (code, patch) => {
    if (lebt.current) {
      setEintraege((alt) => alt.map((e) => (e.code === code ? { ...e, ...patch } : e)));
    }
  };

  const meins = (code) => {
    const norm = dpdCodeAusTracking(code) || String(code || '').trim();
    if (!norm) return null;
    return (paketeRef.current || []).find((p) => {
      if (!p || p.deleted || p.ausgegebenAm || p.zurueckgeschickt) return false;
      const pt = dpdCodeAusTracking(p.trackingNumber) || String(p.trackingNumber || '').trim();
      return pt && pt === norm;
    });
  };

  const [turbo] = useState(() => isTurboOn());
  const [turboStand, setTurboStand] = useState({ letzter: null, gebucht: 0, ausnahmen: 0, summeMs: 0, n: 0 });
  const scanZeit = useRef(new Map());
  const turboMelden = (letzter, dauerMs) => {
    if (!turbo || !lebt.current) return;
    setTurboStand((s) => ({
      letzter,
      gebucht: s.gebucht + (letzter.ausnahme === 'fremd' || letzter.ausnahme === 'doppelt' ? 0 : 1),
      ausnahmen: s.ausnahmen + (letzter.ausnahme ? 1 : 0),
      summeMs: s.summeMs + (dauerMs || 0),
      n: s.n + (dauerMs ? 1 : 0),
    }));
  };

  const verarbeite = async (code, rawBarcode) => {
    const foto = fotos.current.get(code) || '';
    fotos.current.delete(code);
    const t0 = scanZeit.current.get(code) || Date.now();
    scanZeit.current.delete(code);

    const r = await mitWiederholung(
      () => dpdAnnahme(rawBarcode || code),
      { onWarte: () => setze(code, { stand: 'wartet', grund: T.serverWarte }) }
    );
    setze(code, { grund: '' });
    const tGebucht = Date.now();
    if (!lebt.current) return;

    if (r && r.reason === 'fremd') {
      tonFehler();
      setze(code, { stand: 'fremd', grund: r.message || '' });
      turboMelden({ fach: '✗', name: '', kurz: dpdKurz(code), ausnahme: 'fremd' });
      return;
    }

    const echtesName = (r && r.ok && r.name) || bekannterDpdName(code) || '';
    const name = echtesName || dpdPlatzhalterName(code);
    const fachJetzt = fachRef.current.trim() || (autoNachnameDpd() && echtesName ? autoRegalFach(echtesName) || '' : '');

    try {
      const neu = await anlegenRef.current({
        tracking: code,
        name,
        regalplatz: fachJetzt,
        dienst: 'DPD',
        imageUrl: '',
        drucken: true,
      });
      const tAngelegt = Date.now();

      if (foto && neu && neu.id) {
        verfolgeFoto(
          fotoSichern(foto).then(async (id) => {
            if (!id || !namenRef.current) return !!id;
            const p = (paketeRef.current || []).find((x) => x && x.id === neu.id) || neu;
            await namenRef.current([{ paket: p, code: dpdKurz(code), name: p.name || name, imageUrl: id, nurFoto: true }]);
            return true;
          }).catch(() => false)
        );
      }

      const zeiten = turbo ? `Turbo: gebucht ${tGebucht - t0} ms, angelegt ${tAngelegt - t0} ms` : '';
      dpdNotiz('annahme_paketpilot', code, true, '', zeiten);
      if (!r || !r.ok) {
        dpdNotiz('rueckfall', code, false, (r && r.reason) || 'unbekannt', 'Annahme: in PaketPilot angelegt, bei DPD Pickup nicht bestätigt');
      }

      const keinFach = !fachJetzt && autoNachnameDpd();
      (r && r.ok ? (keinFach ? tonWarn : tonOk) : tonFehler)();
      setze(code, {
        stand: r && r.ok ? (r.alreadyDelivered ? 'schon' : 'ok') : 'ohne',
        name,
        fach: fachJetzt,
        grund: (r && (r.message || r.reason)) || '',
      });
      const ausnahme = !(r && r.ok) ? 'rueckfall' : !echtesName ? 'keinName' : keinFach ? 'keinFach' : '';
      turboMelden({ fach: fachJetzt, name, kurz: dpdKurz(code), ausnahme }, tAngelegt - t0);
    } catch (e) {
      tonFehler();
      dpdNotiz('annahme_paketpilot', code, false, 'fehler', String((e && e.message) || e).slice(0, 200));
      setze(code, { stand: 'ohne', name, grund: 'paketpilot' });
      turboMelden({ fach: fachJetzt, name, kurz: dpdKurz(code), ausnahme: 'rueckfall' });
    }
  };

  const nochmal = (code, rawBarcode) => {
    setze(code, { stand: 'wartet', grund: '' });
    kette.current = kette.current.then(async () => {
      const r = await dpdAnnahme(rawBarcode || code);
      if (!lebt.current) return;
      if (!r || !r.ok) {
        tonFehler();
        setze(code, { stand: r && r.reason === 'fremd' ? 'fremd' : 'ohne', grund: (r && (r.message || r.reason)) || '' });
        return;
      }
      tonOk();
      const p = meins(code);
      if (p && r.name && namenRef.current) {
        try { await namenRef.current([{ paket: p, code: dpdKurz(code), name: r.name }]); } catch { /* */ }
      }
      dpdNotiz('annahme_paketpilot', code, true, '', 'nach Rückfall erneut an DPD Pickup gegeben');
      setze(code, { stand: 'ok', name: r.name || bekannterDpdName(code) || dpdPlatzhalterName(code), grund: '' });
    }).catch(() => {});
  };

  const druckenNochmal = (code) => {
    const p = meins(code);
    if (!p) return;
    window.dispatchEvent(new CustomEvent('kiosk:reprint-label', { detail: p }));
    setze(code, { gedruckt: true });
  };

  const neuerCode = (roh, foto, vollFoto, alle) => {
    const liste = Array.isArray(alle) && alle.length ? alle : [roh];
    // Bevorzuge 27/28-stelligen DPD-Leitcode bzw. Aztec-Code, falls auf demselben Label mehrere Codes erkannt wurden
    const trefferRoh = liste.find(istDpdLangBarcode) || liste.find(istDpdScanCode) || '';
    const code = dpdCodeAusTracking(trefferRoh);
    if (!code || gesehen.current.has(code)) return;
    gesehen.current.add(code);
    scanZeit.current.set(code, Date.now());
    if (vollFoto || foto) fotos.current.set(code, vollFoto || foto);

    const vorhandenes = meins(code);
    const schonDa = !!vorhandenes;
    const vorabName = (vorhandenes && vorhandenes.name) || bekannterDpdName(code) || '';
    const rawClean = dpdScanBarcode(trefferRoh || code);
    if (schonDa && turbo) {
      turboMelden({ fach: (vorhandenes && vorhandenes.regalplatz) || '—', name: vorabName, kurz: dpdKurz(code), ausnahme: 'doppelt' });
    }
    setEintraege((alt) => [{
      code,
      rawCode: rawClean,
      stand: schonDa ? 'doppelt' : 'wartet',
      name: vorabName,
      fach: (vorhandenes && vorhandenes.regalplatz) || '',
      grund: '',
    }, ...alt]);
    if (!schonDa) {
      kette.current = kette.current.then(() => verarbeite(code, rawClean)).catch(() => {});
    }
  };

  const standText = (e) => ({
    wartet: `⏳ ${T.wartet}`,
    ok: `✅ ${T.okDpd}`,
    schon: `ℹ️ ${T.schon}`,
    ohne: `⚠️ ${T.ohneDpd}`,
    fremd: `✗ ${T.fremd}`,
    doppelt: `↩️ ${T.doppelt}`,
  }[e.stand] || '');

  const vorText = vorStand.laeuft
    ? `⏳ ${T.vorLaeuft}`
    : !vorStand.ok
      ? `⚠️ ${T.vorFehler}`
      : vorStand.count > 0
        ? `✅ ${vorStand.count} ${T.vorOk}`
        : `ℹ️ ${T.vorLeer}`;

  const feld = {
    width: '100%',
    padding: '10px',
    borderRadius: 8,
    border: `1px solid ${f.rand}`,
    fontSize: 15,
    background: f.feld,
    color: f.text,
    boxSizing: 'border-box',
  };
  const laeuft = eintraege.some((e) => e.stand === 'wartet');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="dpd-erfassen" style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <b style={{ fontSize: 18 }}>📦 {T.erfassenTitel}</b>
        <div data-testid="dpd-live" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#dc0032', color: '#fff', fontSize: 13, fontWeight: 700 }}>
          🔴 {T.liveBadge}
        </div>
        <div style={{ fontSize: 13, color: f.sub, marginTop: 6, lineHeight: 1.5 }}>{T.erfassenHinweis}</div>
        {vorText && (
          <div data-testid="dpd-vorladen" style={{ fontSize: 12.5, color: vorStand.ok ? (dark ? '#86efac' : '#166534') : f.sub, marginTop: 6, fontWeight: 600 }}>
            {vorText}
          </div>
        )}
        {turbo && (
          <TurboAnzeige
            language={language}
            dark={dark}
            letzter={turboStand.letzter}
            gebucht={turboStand.gebucht}
            ausnahmen={turboStand.ausnahmen}
            schnittMs={turboStand.n ? turboStand.summeMs / turboStand.n : 0}
            wartend={eintraege.filter((e) => e.stand === 'wartet').length}
          />
        )}
        <FotoStand stand={fotoStand} dark={dark} text={(a, b) => `${T.fotosLaden} (${a}/${b})`} fehlerText={(n) => `${n} ${T.fotosFehler}`} />
        <input
          data-testid="dpd-fach"
          placeholder={isAutoRegalLetter() ? T.fachAuto : T.fach}
          value={fach}
          onChange={(e) => setFach(e.target.value)}
          style={{ ...feld, marginTop: 12 }}
        />
        <div style={{ marginTop: 12 }}>
          <ShelfQrScanner
            active
            eingebettet
            mitFoto
            vollbildMoeglich
            filter={istDpdScanCode}
            formats={['code_128', 'aztec', 'qr_code', 'itf', 'data_matrix', 'code_39', 'codabar', 'pdf417']}
            onCode={(text, alle, foto, vollFoto) => neuerCode(text, foto, vollFoto, alle)}
            vollbildInfo={
              eintraege.length || fotoStand.gesamt
                ? [
                    <FotoStand key="fotos" hell stand={fotoStand} text={(a, b) => `${T.fotosLaden} (${a}/${b})`} fehlerText={(n) => `${n} ${T.fotosFehler}`} />,
                    ...eintraege.slice(0, 3).map((e, i) => (
                      <div key={e.code} style={{ padding: '5px 0', fontSize: i === 0 ? 17 : 14, opacity: i === 0 ? 1 : 0.75, borderTop: i ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                        <b>{dpdKurz(e.code)}</b>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
                        <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'fremd' ? '#ff8a80' : '#cfd8dc' }}>{standText(e)}</div>
                      </div>
                    )),
                  ]
                : null
            }
          />
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            neuerCode(hand, '', '', [hand]);
            setHand('');
          }}
          style={{ display: 'flex', gap: 8, marginTop: 12 }}
        >
          <input data-testid="dpd-hand" placeholder={T.codeTippen} value={hand} onChange={(e) => setHand(e.target.value)} style={feld} />
          <button type="submit" data-testid="dpd-hand-ok" style={{ padding: '0 14px', borderRadius: 8, border: 'none', background: '#dc0032', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {T.hinzufuegen}
          </button>
        </form>
        <div data-testid="dpd-liste" style={{ marginTop: 12 }}>
          {eintraege.map((e) => (
            <div key={e.code} data-stand={e.stand} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 14 }}>
              <b>{dpdKurz(e.code)}</b> <span style={{ color: f.sub, fontSize: 12 }}>{e.code}</span>{e.name ? ` · ${e.name}` : ''}{e.fach ? ` · 📍 ${e.fach}` : ''}
              <div style={{ fontSize: 13, color: e.stand === 'ohne' || e.stand === 'fremd' ? '#c62828' : f.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>{standText(e)}{e.grund && e.stand !== 'ok' && e.stand !== 'schon' ? ` (${e.grund})` : ''}</span>
                {e.stand === 'ohne' && (
                  <button type="button" data-testid={`dpd-nochmal-${e.code}`} onClick={() => nochmal(e.code, e.rawCode)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' }}>
                    ↻ {T.nochmal}
                  </button>
                )}
                {e.stand === 'doppelt' && (
                  <button type="button" data-testid={`dpd-drucken-${e.code}`} onClick={() => druckenNochmal(e.code)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${f.rand}`, background: 'transparent', color: f.text, fontSize: 13, cursor: 'pointer' }}>
                    🖨️ {e.gedruckt ? '✓' : 'Label'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="dpd-schliessen"
          disabled={laeuft}
          onClick={() => {
            const n = fotosOffen(fotoStand);
            if (n && !window.confirm(`${n} ${T.fotosOffen}`)) return;
            onClose();
          }}
          style={{ width: '100%', padding: '12px', borderRadius: 10, marginTop: 14, fontSize: 15, fontWeight: 600, cursor: laeuft ? 'default' : 'pointer', border: 'none', background: '#dc0032', color: '#fff', opacity: laeuft ? 0.6 : 1 }}
        >
          {laeuft ? `⏳ ${T.wartet}` : T.fertig}
        </button>
      </div>
    </div>
  );
}

